/**
 * Jobs Page - Full job management for Company administrators
 */

import { useState } from "react";
import { PageHeader, Tabs } from "../components/common";
import { QuickActionButton } from "../components/dashboard";
import { OneTimeJobsList } from "../components/jobs/OneTimeJobsList";
import { RecurringSchedulesList } from "../components/jobs/RecurringSchedulesList";
import { CreateJobModal } from "../components/modals";
import { CreateRecurringScheduleModal } from "../components/modals/schedule/CreateRecurringScheduleModal";
import { useAuth } from "../hooks/useAuth";

type TabType = "one-time" | "recurring";

export function Jobs() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>("one-time");
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [showCreateSchedule, setShowCreateSchedule] = useState(false);
  const [jobsRefreshTrigger, setJobsRefreshTrigger] = useState(0);
  const [schedulesRefreshTrigger, setSchedulesRefreshTrigger] = useState(0);

  if (!currentUser?.companyId) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>
        No company associated with this account.
      </div>
    );
  }

  return (
    <div style={{ padding: "32px" }}>
      <PageHeader
        title="Job Management"
        subtitle="Create, assign, and manage job assignments for your team"
        actions={
          <QuickActionButton
            label={activeTab === "one-time" ? "Create New One-time Job" : "Create New Recurring Schedule"}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>}
            onClick={() => activeTab === "one-time" ? setShowCreateJob(true) : setShowCreateSchedule(true)}
            variant="secondary"
            style={{ whiteSpace: "nowrap" }}
          />
        }
      />

      <Tabs
        tabs={[
          { value: "one-time", label: "One-time Jobs" },
          { value: "recurring", label: "Recurring Schedules" },
        ]}
        active={activeTab}
        onChange={setActiveTab}
        variant="pill"
      />

      {/* Content */}
      {activeTab === "one-time" ? (
        <OneTimeJobsList
          companyId={currentUser.companyId}
          refreshTrigger={jobsRefreshTrigger}
          companyTimezone={currentUser.companyTimezone}
        />
      ) : (
        <RecurringSchedulesList
          companyId={currentUser.companyId}
          refreshTrigger={schedulesRefreshTrigger}
          companyTimezone={currentUser.companyTimezone}
        />
      )}

      {/* Create Job Modal */}
      <CreateJobModal
        isOpen={showCreateJob}
        onClose={() => setShowCreateJob(false)}
        companyId={currentUser.companyId}
        onSuccess={() => {
          setJobsRefreshTrigger((prev) => prev + 1);
        }}
      />

      {/* Create Recurring Schedule Modal */}
      <CreateRecurringScheduleModal
        isOpen={showCreateSchedule}
        onClose={() => setShowCreateSchedule(false)}
        companyId={currentUser.companyId}
        onSuccess={() => {
          setSchedulesRefreshTrigger((prev) => prev + 1);
        }}
      />
    </div>
  );
}
